import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { AuthService } from '../api/services/authService';
import { OnboardingService } from '../api/services/onboardingService';
import { useUWStore } from './uwStore';
import { getStoredTokens, hasValidAuth } from '../lib/tokenUtils';
import { resetLogoutSource, setLogoutSource, type LogoutSource } from '../lib/logoutSource';
import { hardClearClientState } from '../lib/clearClientState';
import type { LoginRequest, User } from '../api/types';
import { identifyClarityUser } from '../logging/clarity';

// Type definitions

interface Alert {
  id: string;
  severity: 'success' | 'info' | 'warning' | 'error';
  message: string;
  timestamp: number;
  title?: string;
  autoHide?: boolean;
  duration?: number;
}

interface FormData {
  [key: string]: any;
}

interface ValidationRules {
  [key: string]: any;
}

interface LogoutOptions {
  readonly source?: LogoutSource;
}

interface AuthSlice {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /**
   * When enabled, the user is locked to the Open Banking flow only
   * (no other navigation/actions except logout).
   */
  openBankingOnly: boolean;
  openBankingOnlyUserId: number | null;
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
  setOpenBankingOnly: (enabled: boolean, userId?: number | null) => void;
  login: (credentials: LoginRequest) => Promise<{ success: boolean; user: User }>;
  logout: (options?: LogoutOptions) => Promise<void>;
  initializeAuth: () => Promise<void>;
  completeAuth: (authData: { user: User; token: string; refreshToken?: string }) => void;
}

interface UISlice {
  theme: string;
  sidebarOpen: boolean;
  alerts: Alert[];
  loading: { [key: string]: boolean };
  setTheme: (theme: string) => void;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  addAlert: (alert: Omit<Alert, 'id' | 'timestamp'>) => void;
  removeAlert: (alertId: string) => void;
  clearAlerts: () => void;
  setLoading: (key: string, isLoading: boolean) => void;
  clearLoading: () => void;
}

interface FormSlice {
  forms: { [key: string]: any };
  currentForm: string | null;
  formData: { [key: string]: FormData };
  validation: { [key: string]: ValidationRules };
  setCurrentForm: (formId: string | null) => void;
  setFormData: (formId: string, data: FormData) => void;
  updateFormField: (formId: string, fieldName: string, value: any) => void;
  setValidation: (formId: string, validation: ValidationRules) => void;
  clearForm: (formId: string) => void;
  resetAllForms: () => void;
}

interface AppSlice {
  initialized: boolean;
  clientConfig: any;
  features: { [key: string]: boolean };
  setInitialized: (initialized: boolean) => void;
  setClientConfig: (config: any) => void;
  setFeatures: (features: { [key: string]: boolean }) => void;
  setFeature: (featureName: string, enabled: boolean) => void;
}

type StoreState = AuthSlice & UISlice & FormSlice & AppSlice;

// Auth state slice
const createAuthSlice = (set: any, get: any): AuthSlice => {
  const normalizePartnerFlag = (value: unknown): 'Y' | 'N' => {
    if (value === 'Y' || value === 'y') return 'Y';
    if (value === true || value === 1) return 'Y';
    if (typeof value === 'string') {
      const v = value.trim().toLowerCase();
      if (v === 'y' || v === 'yes' || v === 'true' || v === '1') return 'Y';
    }
    return 'N';
  };

  // Initialize auth state from localStorage on store creation
  const initializeFromStorage = () => {
    const { accessToken } = getStoredTokens();
    const isValid = hasValidAuth();
    
    // Try to restore user data from localStorage
    let user = null;
    try {
      const storedUser = localStorage.getItem('userData');
      if (storedUser && isValid) {
        user = JSON.parse(storedUser);
      }
    } catch (error) {
      console.warn('Failed to parse stored user data:', error);
      localStorage.removeItem('userData');
    }
    
    return {
      token: accessToken,
      isAuthenticated: isValid && user !== null,
      user: user,
      isLoading: false,
      openBankingOnly: false,
      openBankingOnlyUserId: null,
    };
  };

  return {
    // Initialize with persisted state
    ...initializeFromStorage(),
    
    // Auth actions
    setUser: (user) => {
      // Normalize partner flag: if backend doesn't send it, treat as "N"
      const normalisedUser =
        user
          ? ({
              ...user,
              // Backend sometimes returns "y"/"n" (lowercase) - normalize to legacy "Y"/"N"
              isPartner: normalizePartnerFlag((user as User).isPartner),
            } as User)
          : null;
      // Store user data in localStorage when setting user
      if (normalisedUser) {
        localStorage.setItem('userData', JSON.stringify(normalisedUser));
      } else {
        localStorage.removeItem('userData');
      }
      const next: Partial<AuthSlice> = {
        user: normalisedUser,
        isAuthenticated: !!normalisedUser,
      };
      // Guard against cross-user leakage when the store is persisted
      if (!normalisedUser) {
        next.openBankingOnly = false;
        next.openBankingOnlyUserId = null;
      } else {
        const currentLockUserId = get().openBankingOnlyUserId;
        if (
          typeof currentLockUserId === 'number' &&
          currentLockUserId > 0 &&
          currentLockUserId !== normalisedUser.userId
        ) {
          next.openBankingOnly = false;
          next.openBankingOnlyUserId = null;
        }
      }
      set(next, false, 'auth/setUser');
      try { void identifyClarityUser(normalisedUser); } catch {}
    },
    
    setToken: (token) => {
      if (token) {
        localStorage.setItem('authToken', token);
      } else {
        localStorage.removeItem('authToken');
      }
      
      // Check if we have both token and user for authentication
      const { user } = get();
      set({ 
        token, 
        isAuthenticated: token ? hasValidAuth() && user !== null : false 
      }, false, 'auth/setToken');
    },

    setOpenBankingOnly: (enabled, userId) => {
      const resolvedUserId =
        typeof userId === 'number'
          ? userId
          : (typeof get().user?.userId === 'number' ? get().user?.userId : null);
      set(
        {
          openBankingOnly: enabled,
          openBankingOnlyUserId: enabled ? resolvedUserId : null,
        } as Partial<AuthSlice>,
        false,
        'auth/setOpenBankingOnly'
      );
    },
  
  login: async (credentials) => {
    set({ isLoading: true }, false, 'auth/login/start');
    try {
      const response = await AuthService.login(credentials);
      
      if (response.data) {
        get().setUser(response.data.user);
        get().setToken(response.data.token);
        set({ isLoading: false }, false, 'auth/login/success');
        
        return { success: true, user: response.data.user };
      } else {
        throw new Error(response.message || 'Login failed');
      }
    } catch (error) {
      set({ isLoading: false }, false, 'auth/login/error');
      // AuthService already handles FEMA capture, so we don't duplicate it here
      throw error;
    }
  },
  
  logout: async (options) => {
    const source: LogoutSource = options?.source ?? 'system';
    setLogoutSource(source);
    try {
      await AuthService.logout();
    } catch (error) {
      // Continue with logout even if API call fails
      console.warn('Logout API call failed:', error);
    } finally {
      // Always clear local state even if the logout API fails.
      // Clear any in-memory onboarding/customer caches
      try { OnboardingService.clearCustomerCache(); } catch {}
      // Reset UW store to prevent cross-customer data leaks
      try { useUWStore.getState().resetStore(); } catch {}
      // Also reset in-memory form state (Zustand slice)
      try {
        // Using set directly to reset form-related state
        set({
          forms: {},
          currentForm: null,
          formData: {},
          validation: {}
        }, false, 'form/resetAllForms/onLogout');
      } catch {}
      try { get().setUser(null); } catch {}
      try { get().setToken(null); } catch {}
      try { set({ isAuthenticated: false }, false, 'auth/logout'); } catch {}
      try { resetLogoutSource(); } catch {}

      // Hard clear all client-side persisted data (local/session storage + PWA caches/IndexedDB).
      // Also runs before a new login to prevent the previous user's data from leaking into the new session.
      try { await hardClearClientState('logout'); } catch {}
    }
  },
  
  // Simplified initialization - validate existing tokens and restore user data
  initializeAuth: async () => {
    const { accessToken } = getStoredTokens();
    const isValid = hasValidAuth();
    
    if (isValid && accessToken) {
      // Try to restore user data from localStorage
      let user = null;
      try {
        const storedUser = localStorage.getItem('userData');
        if (storedUser) {
          user = JSON.parse(storedUser);
        }
      } catch (error) {
        console.warn('Failed to parse stored user data:', error);
        localStorage.removeItem('userData');
      }
      
      if (user) {
        // User data exists, restore authentication state
        set({ 
          token: accessToken, 
          user: user,
          isAuthenticated: true 
        }, false, 'auth/initialize/valid');
          try { void identifyClarityUser(user as User); } catch {}
      } else {
        // No user data in localStorage - user is not authenticated
        // Clear everything to ensure clean state
        get().setUser(null);
        get().setToken(null);
        localStorage.removeItem('userData');
        localStorage.removeItem('refreshToken');
        set({ isAuthenticated: false }, false, 'auth/initialize/no_user');
      }
    } else {
      // Token invalid/expired, clear everything
      get().setUser(null);
      get().setToken(null);
      localStorage.removeItem('userData');
      localStorage.removeItem('refreshToken');
      set({ isAuthenticated: false }, false, 'auth/initialize/invalid');
    }
  },

  completeAuth: (authData) => {
    // Store user data and token
    get().setUser(authData.user);
    get().setToken(authData.token);
    if (authData.refreshToken) {
      localStorage.setItem('refreshToken', authData.refreshToken);
    }
    set({ isAuthenticated: true }, false, 'auth/completeAuth');
  },
  };
};

// UI state slice
const createUISlice = (set: any): UISlice => ({
  // UI state
  theme: 'light',
  sidebarOpen: false,
  alerts: [],
  loading: {},
  
  // UI actions
  setTheme: (theme) => set({ theme }, false, 'ui/setTheme'),
  
  toggleSidebar: () => set((state: UISlice) => ({
    sidebarOpen: !state.sidebarOpen
  }), false, 'ui/toggleSidebar'),
  
  setSidebarOpen: (open) => set({ sidebarOpen: open }, false, 'ui/setSidebarOpen'),
  
  addAlert: (alert) => set((state: UISlice) => ({
    alerts: [...state.alerts, {
      id: Date.now() + Math.random(),
      timestamp: Date.now(),
      ...alert
    }]
  }), false, 'ui/addAlert'),
  
  removeAlert: (alertId) => set((state: UISlice) => ({
    alerts: state.alerts.filter((alert: Alert) => alert.id !== alertId)
  }), false, 'ui/removeAlert'),
  
  clearAlerts: () => set({ alerts: [] }, false, 'ui/clearAlerts'),
  
  setLoading: (key, isLoading) => set((state: UISlice) => ({
    loading: { ...state.loading, [key]: isLoading }
  }), false, 'ui/setLoading'),
  
  clearLoading: () => set({ loading: {} }, false, 'ui/clearLoading'),
});

// Form state slice
const createFormSlice = (set: any, _get: any): FormSlice => ({
  // Form state
  forms: {},
  currentForm: null,
  formData: {},
  validation: {},
  
  // Form actions
  setCurrentForm: (formId) => {
    set({ currentForm: formId }, false, 'form/setCurrentForm');
  },
  
  setFormData: (formId, data) => {
    set((state: FormSlice) => ({
      formData: { ...state.formData, [formId]: data }
    }), false, 'form/setFormData');
  },
  
  updateFormField: (formId, fieldName, value) => {
    set((state: FormSlice) => ({
      formData: {
        ...state.formData,
        [formId]: {
          ...state.formData[formId],
          [fieldName]: value
        }
      }
    }), false, 'form/updateFormField');
  },
  
  setValidation: (formId, validation) => {
    set((state: FormSlice) => ({
      validation: { ...state.validation, [formId]: validation }
    }), false, 'form/setValidation');
  },
  
  clearForm: (formId) => {
    set((state: FormSlice) => {
      const newFormData = { ...state.formData };
      const newValidation = { ...state.validation };
      delete newFormData[formId];
      delete newValidation[formId];

      return {
        formData: newFormData,
        validation: newValidation,
        currentForm: state.currentForm === formId ? null : state.currentForm
      };
    }, false, 'form/clearForm');
  },
  
  resetAllForms: () => {
    set({
      forms: {},
      currentForm: null,
      formData: {},
      validation: {}
    }, false, 'form/resetAllForms');
  },
});

// Application state slice
const createAppSlice = (set: (partial: Partial<StoreState> | ((state: StoreState) => Partial<StoreState>), replace?: boolean, action?: string) => void): AppSlice => ({
  // App state
  initialized: false,
  clientConfig: null,
  features: {},
  
  // App actions
  setInitialized: (initialized: boolean) => set({ initialized }, false, 'app/setInitialized'),
  
  setClientConfig: (config: any) => set({ clientConfig: config }, false, 'app/setClientConfig'),
  
  setFeatures: (features: { [key: string]: boolean }) => set({ features }, false, 'app/setFeatures'),
  
  setFeature: (featureName: string, enabled: boolean) => set((state: StoreState) => ({
    features: { ...state.features, [featureName]: enabled }
  }), false, 'app/setFeature'),
});

// Create the main store
export const useStore = create<StoreState>()(
  persist(
    devtools(
      (set, get) => ({
        ...createAuthSlice(set, get),
        ...createUISlice(set),
        ...createFormSlice(set, get),
        ...createAppSlice(set),
      }),
      {
        name: 'IE Hub Store',
      }
    ),
    {
      name: 'ie-hub-store',
      partialize: (state: StoreState) => ({
        // Persist UI preferences
        theme: state.theme,
        sidebarOpen: state.sidebarOpen,
        features: state.features,
        // Persist auth state (tokens are in localStorage separately)
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        // Persist Open Banking lock state (non-sensitive)
        openBankingOnly: state.openBankingOnly,
        openBankingOnlyUserId: state.openBankingOnlyUserId,
        // Don't persist sensitive tokens in Zustand (use localStorage directly)
        // Don't persist form data or loading states
      }),
    }
  )
);

// Selectors for better performance
export const useAuth = () => useStore((state) => ({
  user: state.user,
  token: state.token,
  isAuthenticated: state.isAuthenticated,
  isLoading: state.isLoading,
  openBankingOnly: state.openBankingOnly,
  openBankingOnlyUserId: state.openBankingOnlyUserId,
  login: state.login,
  logout: state.logout,
  setUser: state.setUser,
  setToken: state.setToken,
  setOpenBankingOnly: state.setOpenBankingOnly,
  initializeAuth: state.initializeAuth,
  completeAuth: state.completeAuth,
}));

export const useUI = () => useStore((state) => ({
  theme: state.theme,
  sidebarOpen: state.sidebarOpen,
  alerts: state.alerts,
  loading: state.loading,
  setTheme: state.setTheme,
  toggleSidebar: state.toggleSidebar,
  setSidebarOpen: state.setSidebarOpen,
  addAlert: state.addAlert,
  removeAlert: state.removeAlert,
  clearAlerts: state.clearAlerts,
  setLoading: state.setLoading,
  clearLoading: state.clearLoading,
}));

export const useForm = () => useStore((state) => ({
  forms: state.forms,
  currentForm: state.currentForm,
  formData: state.formData,
  validation: state.validation,
  setCurrentForm: state.setCurrentForm,
  setFormData: state.setFormData,
  updateFormField: state.updateFormField,
  setValidation: state.setValidation,
  clearForm: state.clearForm,
  resetAllForms: state.resetAllForms,
}));

export const useApp = () => useStore((state) => ({
  initialized: state.initialized,
  clientConfig: state.clientConfig,
  features: state.features,
  setInitialized: state.setInitialized,
  setClientConfig: state.setClientConfig,
  setFeatures: state.setFeatures,
  setFeature: state.setFeature,
}));

export default useStore;
